import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export default class extends Controller {
  static targets = ["input", "result"]
  static values = {
    url: String,
  }

  connect() {
    this.timeout = null
    this.fetchEstimate()
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
  }

  contentChanged() {
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchEstimate(), 500)
  }

  async fetchEstimate() {
    const content = this.inputTarget.value
    if (!content.trim()) {
      this.resultTarget.innerHTML = this.emptyState()
      return
    }

    try {
      const response = await get(this.urlValue, {
        query: { content },
        responseKind: "json",
      })

      if (!response.ok) return

      const data = await response.json
      this.resultTarget.innerHTML = this.renderEstimate(data)
    } catch {
      // silently fail — estimate is non-critical
    }
  }

  renderEstimate(data) {
    const allSame =
      data.min.segments === data.max.segments &&
      data.min.characters === data.max.characters

    if (allSame) {
      return this.renderSingle(data.min)
    }

    return this.renderExpanded(data)
  }

  renderSingle(result) {
    const colorClasses = this.colorClasses(result.color)

    return `
      <div class="rounded-lg border ${colorClasses.border} p-3 text-sm">
        <div class="flex items-center justify-between mb-1">
          <span class="font-medium text-gray-700 dark:text-neutral-300">SMS Length</span>
          <span class="font-semibold ${colorClasses.text}">${result.segments} segment${result.segments !== 1 ? "s" : ""}</span>
        </div>
        <div class="text-xs text-gray-500 dark:text-neutral-400">${result.characters} characters · Shorter messages get more responses. Messages over 160 characters use extra segments, which may increase messaging costs.</div>
      </div>
    `
  }

  renderExpanded(data) {
    return `
      <div class="text-sm">
        <div class="text-xs text-gray-500 dark:text-neutral-400 mb-2">Shorter messages get more responses. Length varies because variables (e.g. names) expand differently. Extra segments may increase messaging costs.</div>
        <div class="grid grid-cols-3 gap-3">
          ${this.renderColumn("Shortest", data.min)}
          ${this.renderColumn("Typical", data.avg)}
          ${this.renderColumn("Longest", data.max)}
        </div>
      </div>
    `
  }

  renderColumn(label, result) {
    const colorClasses = this.colorClasses(result.color)

    return `
      <div class="rounded-lg border ${colorClasses.border} p-3">
        <div class="text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">${label}</div>
        <div class="text-lg font-semibold ${colorClasses.text}">${result.segments} segment${result.segments !== 1 ? "s" : ""}</div>
        <div class="text-xs text-gray-500 dark:text-neutral-400">${result.characters} chars</div>
      </div>
    `
  }

  colorClasses(color) {
    switch (color) {
      case "green":
        return {
          border:
            "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20",
          text: "text-green-700 dark:text-green-400",
        }
      case "yellow":
        return {
          border:
            "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20",
          text: "text-yellow-700 dark:text-yellow-400",
        }
      case "red":
        return {
          border:
            "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20",
          text: "text-red-700 dark:text-red-400",
        }
      default:
        return {
          border: "border-gray-200 dark:border-neutral-700",
          text: "text-gray-800 dark:text-neutral-200",
        }
    }
  }

  emptyState() {
    return `<p class="text-sm text-gray-400 dark:text-neutral-500">Type content to see SMS segment estimate.</p>`
  }
}
